import { AVATAR_ID, EditablePreviewWorld, Vec3 } from '../types'
import { playSample } from './audio'
import { FRAME_SECONDS } from './constants'
import { CANNON_BEAM_SFX, TARGET_HIT_SFX } from './missionSfx'
import { MATO_NAME, MATO_SCORE_BY_SIZE, runCreateMato } from './rjf05Mato'
import { runShipWaves } from './rjf05Ship'
import { findStateByName, nextTempId } from './sharedHelpers'
import {
  BehaviorContext,
  BehaviorHandler,
  HudStyle,
  MissionBehaviors,
} from './types'

/**
 * RJF05 のミッション環境ビヘイビア。
 *
 * テンプレート rbxl（src/missions/templates/RJF05.rbxl）の大砲
 * （Workspace/Cannon: ROBLOX Pirate Cannon）を移植する。実機は
 * ClickDetector 経由のクリックで RemoteEvent を発火し、砲身（Barrel）の
 * -Z 方向へ砲弾（Ball 3x3x3, Velocity=200）を射出する。砲弾は通常の物理
 * パーツとして重力で弾道を描き、地形や部品と衝突する。砲口に炎（0.5 秒）と
 * 煙（3 秒）、発射音（paintball.wav, Volume 0.2）が出て、発射クールダウンは
 * 2 秒、砲弾は生成 0.1 秒後に可視化され 5 秒で消滅する。
 *
 * 注: 元の CannonballScript には爆発処理（OnExplosionHit）が定義だけ残って
 * いるが、Touched/Explosion への配線が無いデッドコードのため移植しない
 * （実機でも砲弾は爆発せず、物理衝突するだけ）。
 *
 * プレビューでの近似・意図的な差異:
 * - 発射音・命中音は実音源（public/sounds/）を鳴らす（missionSfx.ts に定義。
 *   元のビルトイン音源 paintball.wav / victory.wav はアセット取得不可）。
 * - 煙は Smoke パーティクルの代わりに、発射方向へ吹き出して漂いながら膨らみ
 *   薄れる半透明の球の群れ（バースト）で表現する。
 * - 砲口の炎は Fire パーティクルの代わりに、一瞬膨らんで消える発光球
 *   （マズルフラッシュ）で表現する。
 * - 実機の砲弾は生成 0.1 秒後に可視化されるが、プレビューでは弾道を最初から
 *   見せるため即時可視にし、飛行中は軌跡（薄れて消える小球のトレイル）を
 *   描く（学習者が弾道を目で追えるようにする意図的な差異）。
 *
 * あわせて Cutter 船（Workspace/Cutter: qShipManager）の待機時の波の揺れ
 * （rjf05Ship.ts）と、マト当てゲーム（rjf05Mato.ts）を移植する。本ファイルは
 * 800 行を超えたため、独立したギミックごとにファイル分割している。
 */

/** 大砲の砲身のオブジェクト名（rbxl: Workspace/Cannon/Barrel）。 */
const BARREL_NAME = 'Barrel'

/** クリックを受け付ける最大距離（Configurations.MaxClickDistance = 40 studs）。 */
const MAX_CLICK_DISTANCE = 40

/** 発射クールダウン（Configurations.FireCooldown = 2 秒）。 */
const FIRE_COOLDOWN_SECONDS = 2

/** 砲弾の初速（Configurations.CannonballSpeed = 200 studs/秒）。 */
const CANNONBALL_SPEED = 200

/** 砲弾の寿命（Lua: wait(5) → Destroy）。 */
const CANNONBALL_LIFETIME_SECONDS = 5

/** 弾道トレイル 1 粒の寿命（秒）。薄れながら縮んで消える。 */
const TRAIL_PUFF_LIFETIME_SECONDS = 0.6

/** 弾道トレイルの生成間隔（秒）。砲弾の足跡として弧を描ける密度。 */
const TRAIL_INTERVAL_SECONDS = 2 / 30

/** 弾道トレイル 1 粒の初期寸法（studs）と色（薄い白煙）。 */
const TRAIL_PUFF_SIZE = 1.4
const TRAIL_PUFF_COLOR = '#DCDCDC'
const TRAIL_PUFF_START_TRANSPARENCY = 0.55

/** 砲弾の寸法（ServerStorage.Part: Ball 3x3x3）。 */
const CANNONBALL_SIZE: Vec3 = { x: 3, y: 3, z: 3 }

/** 砲弾の色（鉄球らしい黒鉄色）。 */
const CANNONBALL_COLOR = '#212121'

/**
 * マズルフラッシュの寿命（秒）。実機の Fire は 0.5 秒残るが、砲の発炎としては
 * 「一瞬光って消える」方が自然なため短い閃光にする。
 */
const MUZZLE_FLASH_SECONDS = 0.22

/** マズルフラッシュの初期直径と最大直径（studs）。膨張しながら消える。 */
const MUZZLE_FLASH_START_SIZE = 1.5
const MUZZLE_FLASH_END_SIZE = 5

/** 発射の煙バーストの粒数。砲口から発射方向へ吹き出す。 */
const SMOKE_BURST_COUNT = 8

/** 煙 1 粒の寿命（秒）。この範囲の乱数で粒ごとにばらす。 */
const SMOKE_PUFF_LIFETIME_MIN_SECONDS = 1.0
const SMOKE_PUFF_LIFETIME_MAX_SECONDS = 1.8

/** 煙の吹き出し初速（studs/秒）。発射方向成分と散らばり成分。 */
const SMOKE_EJECT_SPEED = 12
const SMOKE_SPREAD_SPEED = 4

/** 煙の速度減衰（1/秒）。吹き出した後すぐ失速して漂う。 */
const SMOKE_DRAG = 2.5

/** 煙の上昇速度（Lua: Smoke.RiseVelocity = 10 を漂い向けに弱めた値）。 */
const SMOKE_RISE_SPEED = 4

/** 煙の初期寸法と成長率（膨らみながら薄れる）。 */
const SMOKE_PUFF_START_SIZE = 1.5
const SMOKE_PUFF_GROWTH_PER_SECOND = 3

/** 煙の透明度（Lua: Smoke.Opacity = .25 → Transparency 0.75 起点でフェード）。 */
const SMOKE_PUFF_START_TRANSPARENCY = 0.75

/** 煙の色（白煙）。 */
const SMOKE_COLOR = '#C8C8C8'

/**
 * 砲弾の発射位置の砲身先端からのオフセット係数。
 * Lua: Position = barrel.Position + (2 + barrel.Size.Z/2) * direction
 */
const MUZZLE_OFFSET_EXTRA = 2

/** クォータニオン {x,y,z,w} でベクトルを回転する（v' = q v q^-1）。 */
const rotateByQuaternion = (
  v: Vec3,
  q: { x: number; y: number; z: number; w: number },
): Vec3 => {
  // t = 2 * cross(q.xyz, v); v' = v + q.w * t + cross(q.xyz, t)
  const tx = 2 * (q.y * v.z - q.z * v.y)
  const ty = 2 * (q.z * v.x - q.x * v.z)
  const tz = 2 * (q.x * v.y - q.y * v.x)
  return {
    x: v.x + q.w * tx + (q.y * tz - q.z * ty),
    y: v.y + q.w * ty + (q.z * tx - q.x * tz),
    z: v.z + q.w * tz + (q.x * ty - q.y * tx),
  }
}

/** 発射音を再生する（実機の paintball.wav に対応する実音源）。 */
const playFireSound = (signal: AbortSignal): void => {
  playSample(CANNON_BEAM_SFX.url, CANNON_BEAM_SFX.volume, signal)
}

/**
 * 砲口の閃光（マズルフラッシュ）を生成する。発光球が一瞬で膨らみながら
 * 薄れて消える（静止した火の玉が 0.5 秒残るより発砲らしい）。
 * 寿命満了・中断のどちらでも finally で必ず破棄する。
 */
const spawnMuzzleFlash = (ctx: BehaviorContext, position: Vec3): void => {
  const { world } = ctx
  const id = nextTempId(world, 'rjf05_muzzle_fire')
  world.addObject({
    id,
    name: `MuzzleFire#${id}`,
    position: { ...position },
    size: {
      x: MUZZLE_FLASH_START_SIZE,
      y: MUZZLE_FLASH_START_SIZE,
      z: MUZZLE_FLASH_START_SIZE,
    },
    color: '#FFC24A',
    material: 'Neon',
    shape: 'ball',
    isStatic: true,
    canCollide: false,
    canTouch: false,
  })
  ;(async () => {
    try {
      const bornMs = performance.now()
      const lifetimeMs = MUZZLE_FLASH_SECONDS * 1000
      for (;;) {
        if (ctx.signal.aborted || !world.exists(id)) {
          return
        }
        const t = Math.min(1, (performance.now() - bornMs) / lifetimeMs)
        if (t >= 1) {
          return
        }
        // 急速に膨張しつつ（ease-out）、後半で一気に透けて消える。
        const eased = 1 - (1 - t) * (1 - t)
        const diameter =
          MUZZLE_FLASH_START_SIZE +
          (MUZZLE_FLASH_END_SIZE - MUZZLE_FLASH_START_SIZE) * eased
        world.setSize(id, { x: diameter, y: diameter, z: diameter })
        world.setTransparency(id, t * t)
        await ctx.wait(FRAME_SECONDS)
      }
    } finally {
      if (world.exists(id)) {
        world.destroy(id)
      }
    }
  })().catch((error: unknown) => {
    console.error('[preview] 砲口の閃光エフェクトの実行中にエラー', error)
  })
}

/**
 * 煙 1 粒を生成して、初速で吹き出し→失速して漂いながら膨らみ・薄れて
 * 消えるまで動かす。中断・例外のどの経路でも finally で必ず破棄する。
 */
const spawnSmokePuff = async (
  ctx: BehaviorContext,
  origin: Vec3,
  velocity: Vec3,
  lifetimeSeconds: number,
): Promise<void> => {
  const { world } = ctx
  const id = nextTempId(world, 'rjf05_smoke')
  const position = { ...origin }
  world.addObject({
    id,
    name: `Smoke#${id}`,
    position: { ...position },
    size: {
      x: SMOKE_PUFF_START_SIZE,
      y: SMOKE_PUFF_START_SIZE,
      z: SMOKE_PUFF_START_SIZE,
    },
    color: SMOKE_COLOR,
    material: 'SmoothPlastic',
    shape: 'ball',
    transparency: SMOKE_PUFF_START_TRANSPARENCY,
    isStatic: true,
    canCollide: false,
    canTouch: false,
  })
  try {
    // 寿命は実時間（wall-clock）で進める。フレーム数基準だとタブ非アクティブ等で
    // タイマーが間引かれた際に煙が何十秒も残る（rjf01 の火の玉と同じ方針）。
    const bornMs = performance.now()
    const lifetimeMs = lifetimeSeconds * 1000
    let lastMs = bornMs
    let currentVelocity = { ...velocity }
    for (;;) {
      if (ctx.signal.aborted || !world.exists(id)) {
        return
      }
      const nowMs = performance.now()
      const t = Math.min(1, (nowMs - bornMs) / lifetimeMs)
      if (t >= 1) {
        return
      }
      const dt = Math.min(0.1, (nowMs - lastMs) / 1000)
      lastMs = nowMs
      // 吹き出し初速は指数減衰で失速し、ゆっくり上昇する漂いに移行する。
      const damping = Math.exp(-SMOKE_DRAG * dt)
      currentVelocity = {
        x: currentVelocity.x * damping,
        y: currentVelocity.y * damping,
        z: currentVelocity.z * damping,
      }
      position.x += currentVelocity.x * dt
      position.y += (currentVelocity.y + SMOKE_RISE_SPEED) * dt
      position.z += currentVelocity.z * dt
      world.setPosition(id, { ...position })
      const diameter =
        SMOKE_PUFF_START_SIZE +
        SMOKE_PUFF_GROWTH_PER_SECOND * t * lifetimeSeconds
      world.setSize(id, { x: diameter, y: diameter, z: diameter })
      // 残り寿命に応じて完全透明へフェードアウトする。
      world.setTransparency(
        id,
        SMOKE_PUFF_START_TRANSPARENCY + (1 - SMOKE_PUFF_START_TRANSPARENCY) * t,
      )
      await ctx.wait(FRAME_SECONDS)
    }
  } finally {
    if (world.exists(id)) {
      world.destroy(id)
    }
  }
}

/**
 * 発射の煙バースト。砲口から発射方向へ吹き出す粒の群れを一斉に生成する
 * （一定間隔で同じ場所に湧く煙柱より発砲の煙らしい）。
 */
const runSmoke = async (
  ctx: BehaviorContext,
  origin: Vec3,
  direction: Vec3,
): Promise<void> => {
  for (let i = 0; i < SMOKE_BURST_COUNT; i += 1) {
    if (ctx.signal.aborted) {
      return
    }
    const spread = () => (Math.random() - 0.5) * 2 * SMOKE_SPREAD_SPEED
    const ejectSpeed = SMOKE_EJECT_SPEED * (0.6 + Math.random() * 0.8)
    const velocity = {
      x: direction.x * ejectSpeed + spread(),
      y: direction.y * ejectSpeed + spread() * 0.5,
      z: direction.z * ejectSpeed + spread(),
    }
    const lifetimeSeconds =
      SMOKE_PUFF_LIFETIME_MIN_SECONDS +
      Math.random() *
        (SMOKE_PUFF_LIFETIME_MAX_SECONDS - SMOKE_PUFF_LIFETIME_MIN_SECONDS)
    spawnSmokePuff(ctx, origin, velocity, lifetimeSeconds).catch(
      (error: unknown) => {
        console.error('[preview] 砲口の煙エフェクトの実行中にエラー', error)
      },
    )
    // 全粒同時だと 1 つの塊に見えるため、ごく短い間隔でずらして湧かせる。
    await ctx.wait(FRAME_SECONDS)
  }
}

/** 勝利音（マト命中）を再生する（実機の victory.wav に対応する実音源）。 */
const playVictorySound = (signal: AbortSignal): void => {
  playSample(TARGET_HIT_SFX.url, TARGET_HIT_SFX.volume, signal)
}

/**
 * 砲弾とマト（名前が「③マト」で始まるオブジェクト）の命中判定。
 * 実機の MatoScript は Touched 相手が ObjectValue 'Value' を持つ＝砲弾のとき
 * だけ反応して自身を Destroy する。プレビューでは砲弾側から球同士の距離で
 * 判定する。命中時は onHit へ命中したマトの一辺（Size.X）を通知する
 * （実機の MatoScript はサイズ別スコアを leaderstats「スコア」へ加算する。
 * 終了表示で使う）。
 */
const hitMatoIfTouching = (
  world: EditablePreviewWorld,
  cannonballId: string,
  signal: AbortSignal,
  onHit?: (matoSizeX: number) => void,
): boolean => {
  const ball = world.getState().get(cannonballId)
  if (!ball) {
    return false
  }
  for (const state of world.getState().values()) {
    if (!state.name.startsWith(MATO_NAME) || !state.visible) {
      continue
    }
    const dx = state.position.x - ball.position.x
    const dy = state.position.y - ball.position.y
    const dz = state.position.z - ball.position.z
    // ball 形状の半径は物理コライダーと同じ「最小軸 / 2」（physicsController の
    // colliderDescForShape と一致させ、見た目・物理より広い命中判定を避ける）。
    const matoRadius = Math.min(state.size.x, state.size.y, state.size.z) / 2
    const ballRadius = Math.min(ball.size.x, ball.size.y, ball.size.z) / 2
    const hitDistance = matoRadius + ballRadius
    if (dx * dx + dy * dy + dz * dz <= hitDistance * hitDistance) {
      const matoSizeX = state.size.x
      world.destroy(state.id)
      playVictorySound(signal)
      onHit?.(matoSizeX)
      return true
    }
  }
  return false
}

/**
 * 弾道トレイル 1 粒。砲弾の現在位置に置かれ、薄れながら縮んで消える。
 * 中断・例外のどの経路でも finally で必ず破棄する。
 */
const spawnTrailPuff = async (
  ctx: BehaviorContext,
  position: Vec3,
): Promise<void> => {
  const { world } = ctx
  const id = nextTempId(world, 'rjf05_trail')
  world.addObject({
    id,
    name: `Trail#${id}`,
    position: { ...position },
    size: { x: TRAIL_PUFF_SIZE, y: TRAIL_PUFF_SIZE, z: TRAIL_PUFF_SIZE },
    color: TRAIL_PUFF_COLOR,
    material: 'SmoothPlastic',
    shape: 'ball',
    transparency: TRAIL_PUFF_START_TRANSPARENCY,
    isStatic: true,
    canCollide: false,
    canTouch: false,
  })
  try {
    const bornMs = performance.now()
    const lifetimeMs = TRAIL_PUFF_LIFETIME_SECONDS * 1000
    for (;;) {
      if (ctx.signal.aborted || !world.exists(id)) {
        return
      }
      const t = Math.min(1, (performance.now() - bornMs) / lifetimeMs)
      if (t >= 1) {
        return
      }
      // 縮みながら完全透明へ（弾の後ろへ尾を引いて消える見え方）。
      const diameter = TRAIL_PUFF_SIZE * (1 - t * 0.6)
      world.setSize(id, { x: diameter, y: diameter, z: diameter })
      world.setTransparency(
        id,
        TRAIL_PUFF_START_TRANSPARENCY + (1 - TRAIL_PUFF_START_TRANSPARENCY) * t,
      )
      await ctx.wait(FRAME_SECONDS)
    }
  } finally {
    if (world.exists(id)) {
      world.destroy(id)
    }
  }
}

/**
 * 砲弾を 1 発生成する。実機どおり通常の物理パーツ（動的剛体 + 初速）として
 * 射出し、弾道・衝突は物理エンジンに任せる。5 秒で破棄する（Lua の
 * CannonballScript と同じ寿命管理）。飛行中は毎フレーム、マトとの命中判定を
 * 行い（命中したマトは消える）、弾道を目で追えるようトレイルを残す。
 * 実機は生成 0.1 秒後に可視化するが、プレビューでは弾道全体を見せるため
 * 最初から可視にする（ヘッダコメント参照）。
 */
const launchCannonball = async (
  ctx: BehaviorContext,
  origin: Vec3,
  direction: Vec3,
  onHit?: (matoSizeX: number) => void,
): Promise<void> => {
  const { world } = ctx
  const id = nextTempId(world, 'rjf05_cannonball')
  world.addObject({
    id,
    name: `Cannonball#${id}`,
    position: { ...origin },
    size: { ...CANNONBALL_SIZE },
    color: CANNONBALL_COLOR,
    material: 'SmoothPlastic',
    shape: 'ball',
    // 通常の物理パーツ。重力・衝突・反発は物理エンジン任せ（実機と同じ）。
    isStatic: false,
    canCollide: true,
    canTouch: true,
    initialVelocity: {
      x: direction.x * CANNONBALL_SPEED,
      y: direction.y * CANNONBALL_SPEED,
      z: direction.z * CANNONBALL_SPEED,
    },
  })
  try {
    const deadlineMs = performance.now() + CANNONBALL_LIFETIME_SECONDS * 1000
    let lastTrailMs = 0
    while (performance.now() < deadlineMs) {
      if (ctx.signal.aborted || !world.exists(id)) {
        return
      }
      hitMatoIfTouching(world, id, ctx.signal, onHit)
      // 弾道トレイル: 一定間隔で現在位置に薄い煙を残す（fire-and-forget）。
      const nowMs = performance.now()
      if (nowMs - lastTrailMs >= TRAIL_INTERVAL_SECONDS * 1000) {
        lastTrailMs = nowMs
        const state = world.getState().get(id)
        if (state) {
          spawnTrailPuff(ctx, state.position).catch((error: unknown) => {
            console.error('[preview] 弾道トレイルの実行中にエラー', error)
          })
        }
      }
      await ctx.wait(FRAME_SECONDS)
    }
  } finally {
    if (world.exists(id)) {
      world.destroy(id)
    }
  }
}

/**
 * 大砲（Barrel）クリックの発射ハンドラ。
 *
 * Lua 同様に、アバターが MaxClickDistance（40 studs）以内のときだけ受理し、
 * 発射後 FireCooldown（2 秒）の間は連射できない。発射方向は砲身の現在の
 * 向き（-Z のワールド方向）から毎回計算する。
 */
const createFireCannonHandler = (
  onHit?: (ctx: BehaviorContext, matoSizeX: number) => void,
): BehaviorHandler => {
  let canFire = true
  return async (ctx: BehaviorContext): Promise<void> => {
    if (!canFire) {
      return
    }
    const { world } = ctx
    const barrel = findStateByName(world, BARREL_NAME)
    const avatar = world.getState().get(AVATAR_ID)
    if (!barrel || !avatar) {
      return
    }
    const dx = barrel.position.x - avatar.position.x
    const dy = barrel.position.y - avatar.position.y
    const dz = barrel.position.z - avatar.position.z
    if (Math.sqrt(dx * dx + dy * dy + dz * dz) > MAX_CLICK_DISTANCE) {
      return
    }
    canFire = false
    // 例外が起きても大砲がそのプレイ中ずっと無効化されないよう、クールダウンの
    // 復帰は finally で保証する。
    try {
      // 砲身ローカル -Z のワールド方向（Lua: CFrame:vectorToWorldSpace(0,0,-1)）。
      // 実行時の向きは「Y ヨー(rotationY) ∘ orientation」の合成（types.ts 参照）。
      const local = rotateByQuaternion(
        { x: 0, y: 0, z: -1 },
        barrel.orientation,
      )
      const yaw = barrel.rotationY
      const direction = {
        x: local.x * Math.cos(yaw) + local.z * Math.sin(yaw),
        y: local.y,
        z: -local.x * Math.sin(yaw) + local.z * Math.cos(yaw),
      }
      const muzzleDistance = MUZZLE_OFFSET_EXTRA + barrel.size.z / 2
      const origin = {
        x: barrel.position.x + direction.x * muzzleDistance,
        y: barrel.position.y + direction.y * muzzleDistance,
        z: barrel.position.z + direction.z * muzzleDistance,
      }
      playFireSound(ctx.signal)
      spawnMuzzleFlash(ctx, origin)
      // fire-and-forget。runBehavior の例外処理の外で走るためここで捕捉する。
      runSmoke(ctx, origin, direction).catch((error: unknown) => {
        console.error('[preview] 砲口の煙の実行中にエラー', error)
      })
      launchCannonball(ctx, origin, direction, (matoSizeX) =>
        onHit?.(ctx, matoSizeX),
      ).catch((error: unknown) => {
        console.error('[preview] 大砲の砲弾の実行中にエラー', error)
      })
      await ctx.wait(FIRE_COOLDOWN_SECONDS)
    } finally {
      canFire = true
    }
  }
}

/**
 * プレイ開始時に砲身をクリック可能にする。実機の ClickDetector 相当
 * （clickable は既定 false で、レンダラのクリックレイキャスト対象に
 * 入るには World 側で有効化が必要）。
 */
const enableCannonClick: BehaviorHandler = (ctx: BehaviorContext): void => {
  const barrel = findStateByName(ctx.world, BARREL_NAME)
  if (barrel) {
    ctx.world.setClickable(barrel.id, true)
  }
}

/**
 * スコアの表示スタイル。実機ではプレイヤーリスト（leaderstats「スコア」、
 * 画面右上の半透明黒）に出る値で、ScreenGui ではないためその印象に合わせる
 * （RJF09 の「ボールのかず」と同じ流儀）。プレビューの右上は出力ログ
 * （OutputLog）が使うため、カウント類は左上へ寄せる。
 */
const LEADERSTATS_HUD_STYLE: HudStyle = {
  background: 'rgba(0, 0, 0, 0.45)',
  color: '#FFFFFF',
  left: 8,
  top: 8,
}

/** RJF05 の環境ビヘイビアを生成する（プレイ開始毎に呼ばれる）。 */
export const createRjf05Behaviors = (): MissionBehaviors => {
  // スコア（実機 leaderstats「スコア」）。マト命中でサイズ別に加算し、
  // プレイ途中のスコア HUD と終了表示で使う。
  let score = 0
  // ゲーム本編中だけスコア HUD を出す（MatoScoreboard のコメント参照）。
  let isScoreHudActive = false
  const showScoreHud = (ctx: BehaviorContext): void => {
    ctx.setHudText?.(`スコア: ${score}`, LEADERSTATS_HUD_STYLE)
  }
  return {
    onPlay: [
      enableCannonClick,
      runShipWaves,
      (ctx) =>
        runCreateMato(ctx, {
          getScore: () => score,
          onGameStart: (gameCtx) => {
            isScoreHudActive = true
            showScoreHud(gameCtx)
          },
          onGameEnd: () => {
            isScoreHudActive = false
          },
        }),
    ],
    onClick: new Map<string, BehaviorHandler>([
      [
        BARREL_NAME,
        createFireCannonHandler((ctx, matoSizeX) => {
          score += MATO_SCORE_BY_SIZE.get(matoSizeX) ?? 0
          // 実機の leaderstats 更新（MatoScript の +=）と同じく命中の瞬間に
          // 表示へ反映する。
          if (isScoreHudActive) {
            showScoreHud(ctx)
          }
        }),
      ],
    ]),
    // ③マト は実機では ServerStorage（Script 非実行）。本体ではプログラムを
    // 起動せず、クローンにだけ startObjectPrograms で起動する。
    serverStorageTemplates: [MATO_NAME],
  }
}
